import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import type { ExportBatch, SageBridgeCompany } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { fmtUtcLocal } from '@/lib/cutoff'

function ago(seconds: number | null): string {
  if (seconds === null) return 'never'
  if (seconds < 90) return `${seconds} s ago`
  if (seconds < 5400) return `${Math.round(seconds / 60)} min ago`
  if (seconds < 172800) return `${Math.round(seconds / 3600)} h ago`
  return `${Math.round(seconds / 86400)} days ago`
}

/**
 * Sage -> Bridge status.
 *
 * The bridge is a small Windows service on the Sage server (NWLPortalBridge).
 * It writes queued lines into Sage Time Track through Sage's own business
 * objects, as the Sage user crmbridge, and checks in here every 15 s.
 *
 * Admin switches writing on or off per Sage company. NWL (live payroll)
 * cannot be switched on until the cutover is approved -- the API refuses it
 * whatever this screen shows.
 */
export function SageBridgePage() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'Admin'
  const [rows, setRows] = useState<SageBridgeCompany[] | null>(null)
  const [recent, setRecent] = useState<Record<string, ExportBatch[]>>({})
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const r = await api.sageBridge()
      setRows(r)
      const lists = await Promise.all(r.map((c) => api.exportBatches(c.company)))
      setRecent(Object.fromEntries(r.map((c, i) => [c.company, lists[i]])))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the Sage bridge')
    }
  }, [])

  useEffect(() => {
    void load()
    const t = window.setInterval(() => void load(), 15000)
    return () => window.clearInterval(t)
  }, [load])

  async function toggle(c: SageBridgeCompany) {
    const on = !c.writing_on
    if (on && c.company === 'NWL' && !window.confirm('Switch on writing to LIVE PAYROLL (NWL)?')) return
    setSaving(c.company)
    setError(null)
    try {
      setRows(await api.setSageWriting(c.company, on))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not change the switch')
    } finally {
      setSaving(null)
    }
  }

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}
      <div className="card">
        <h2 className="title" style={{ marginTop: 0 }}>Bridge status</h2>
        <p className="muted" style={{ marginBottom: 0 }}>
          The Sage bridge runs on the Sage server and writes the lines sent from{' '}
          <Link to="/office/send-to-sage">Send to Sage</Link> into Sage Time Track, through Sage's own
          rules, as its own Sage user. Lines sent while writing is off wait in the queue.
          {!isAdmin && ' Only an Admin can switch writing on or off.'}
        </p>
      </div>

      {rows === null ? (
        <div className="card"><p className="muted">Loading…</p></div>
      ) : (
        rows.map((c) => (
          <div className="card" key={c.company}>
            <h2 style={{ marginTop: 0 }}>
              {c.company === 'TST' ? 'TST — test company' : 'NWL — live payroll'}{' '}
              {c.running ? (
                <span className="badge badge-ok">Bridge running</span>
              ) : (
                <span className="badge badge-warn">Bridge not running</span>
              )}
            </h2>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={c.writing_on}
                disabled={!isAdmin || saving === c.company || (!c.allowed && !c.writing_on)}
                onChange={() => void toggle(c)}
              />
              <strong>Write to {c.company}</strong>
            </label>
            {!c.allowed && (
              <p className="muted">Not available until the cutover to live payroll is approved.</p>
            )}
            <p className="muted" style={{ marginBottom: 0 }}>
              Last check-in {ago(c.seconds_since_seen)}
              {c.bridge_host && ` from ${c.bridge_host}`}
              {c.bridge_version && `, version ${c.bridge_version}`}
              {c.last_message && ` — ${c.last_message}`}
              <br />
              {c.waiting} line{c.waiting === 1 ? '' : 's'} waiting · {c.in_progress} being written
              {c.updated_by && ` · switch last changed by ${c.updated_by}${c.updated_utc ? `, ${fmtUtcLocal(c.updated_utc)}` : ''}`}
            </p>
            {(recent[c.company] ?? []).length > 0 && (
              <div className="table-wrap" style={{ marginTop: '0.75rem' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Batch</th>
                      <th>Week ending</th>
                      <th>Sent</th>
                      <th>Status</th>
                      <th>Lines</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(recent[c.company] ?? []).map((b) => (
                      <tr key={b.batch_id}>
                        <td>{b.batch_id}</td>
                        <td>{b.week_ending}</td>
                        <td className="muted">
                          {b.approved_by ?? '—'}, {fmtUtcLocal(b.approved_utc)}
                        </td>
                        <td>{b.status === 'Open' ? 'In progress' : b.status}</td>
                        <td className="muted">
                          {Object.entries(b.counts)
                            .map(([k, v]) => `${v} ${k === 'Done' ? 'in Sage' : k === 'Approved' ? 'waiting' : k.toLowerCase()}`)
                            .join(', ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  )
}
